import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { communicationService } from '../../../lib/communicationService';

const PostCard = ({ post, currentUserId, onRefresh, isPinned = false }) => {
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(post?.likes_count || 0);
  const [commentsCount, setCommentsCount] = useState(post?.comments_count || 0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    checkIfLiked();
  }, [post?.id]);

  useEffect(() => {
    if (showComments) {
      loadComments();
    }
  }, [showComments]);

  const checkIfLiked = async () => {
    const { data } = await communicationService?.checkIfUserLikedPost(post?.id);
    setIsLiked(data);
  };

  const loadComments = async () => {
    const { data } = await communicationService?.getPostComments(post?.id);
    if (data) {
      setComments(data);
    }
  };

  const handleLike = async () => {
    const { data } = await communicationService?.togglePostLike(post?.id);
    if (data) {
      setIsLiked(data?.liked);
      setLikesCount(prev => data?.liked ? prev + 1 : prev - 1);
    }
  };

  const handleAddComment = async (e) => {
    e?.preventDefault();
    if (!newComment?.trim()) return;

    setLoading(true);
    const { data, error } = await communicationService?.createComment(post?.id, newComment);
    if (!error && data) {
      setComments(prev => [...prev, data]);
      setCommentsCount(prev => prev + 1);
      setNewComment('');
    }
    setLoading(false);
  };

  const handleDeleteComment = async (commentId) => {
    const { error } = await communicationService?.deleteComment(commentId);
    if (!error) {
      setComments(prev => prev?.filter(c => c?.id !== commentId));
      setCommentsCount(prev => prev - 1);
    }
  };

  const categoryColors = {
    achievement: 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400',
    question: 'bg-blue-500/10 text-blue-700 dark:text-blue-400',
    challenge: 'bg-purple-500/10 text-purple-700 dark:text-purple-400',
    motivation: 'bg-orange-500/10 text-orange-700 dark:text-orange-400',
    event: 'bg-green-500/10 text-green-700 dark:text-green-400',
    general: 'bg-gray-500/10 text-gray-700 dark:text-gray-400'
  };

  const categoryIcons = {
    achievement: 'Trophy',
    question: 'HelpCircle',
    challenge: 'Target',
    motivation: 'Zap',
    event: 'Calendar',
    general: 'MessageSquare'
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${Math.floor(diffInHours)}h ago`;
    if (diffInHours < 48) return 'Yesterday';
    return date?.toLocaleDateString();
  };

  return (
    <div className="bg-card rounded-xl p-6 shadow-md border border-border">
      {/* Header */}
      <div className="flex items-start gap-4 mb-4">
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <Icon name="User" size={24} color="var(--color-primary)" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-foreground">
              {post?.author?.full_name || 'Member'}
            </h3>
            {isPinned && (
              <Icon name="Pin" size={16} color="var(--color-primary)" />
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="capitalize">{post?.author?.role || 'member'}</span>
            <span>•</span>
            <span>{formatDate(post?.created_at)}</span>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${categoryColors?.[post?.category]}`}>
          {post?.category}
        </span>
      </div>
      {/* Content */}
      <div className="mb-4">
        <h4 className="text-lg font-semibold text-foreground mb-2">{post?.title}</h4>
        <p className="text-foreground whitespace-pre-wrap">{post?.content}</p>
      </div>
      {/* Media */}
      {post?.media_url && (
        <div className="mb-4 rounded-lg overflow-hidden">
          <img
            src={post?.media_url}
            alt={post?.title}
            className="w-full h-auto max-h-96 object-cover"
          />
        </div>
      )}
      {/* Actions */}
      <div className="flex items-center gap-4 pt-4 border-t border-border">
        <button
          onClick={handleLike}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            isLiked
              ? 'bg-primary/10 text-primary' :'bg-muted text-foreground hover:bg-muted/80'
          }`}
        >
          <Icon name={isLiked ? 'Heart' : 'Heart'} size={18} fill={isLiked ? 'currentColor' : 'none'} />
          <span>{likesCount}</span>
        </button>
        <button
          onClick={() => setShowComments(!showComments)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-muted text-foreground hover:bg-muted/80 transition-colors"
        >
          <Icon name="MessageCircle" size={18} />
          <span>{commentsCount}</span>
        </button>
      </div>
      {/* Comments Section */}
      {showComments && (
        <div className="mt-4 pt-4 border-t border-border space-y-4">
          {/* Comments List */}
          {comments?.length > 0 && (
            <div className="space-y-3">
              {comments?.map((comment) => (
                <div key={comment?.id} className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                    <Icon name="User" size={16} color="var(--color-muted-foreground)" />
                  </div>
                  <div className="flex-1 bg-muted rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-foreground">
                        {comment?.author?.full_name || 'Member'}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          {formatDate(comment?.created_at)}
                        </span>
                        {comment?.author_id === currentUserId && (
                          <button
                            onClick={() => handleDeleteComment(comment?.id)}
                            className="text-red-500 hover:text-red-600"
                          >
                            <Icon name="Trash2" size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-foreground">{comment?.content}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add Comment Form */}
          <form onSubmit={handleAddComment} className="flex gap-2">
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e?.target?.value)}
              placeholder="Write a comment..."
              className="flex-1 px-4 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={loading}
            />
            <Button
              type="submit"
              disabled={loading || !newComment?.trim()}
              iconName="Send"
            >
              Post
            </Button>
          </form>
        </div>
      )}
    </div>
  );
};

export default PostCard;